<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import STATUS_TONE_MAP from "@/Constans/StatusToneMap";
import KanbanActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import {
  compactClockLabel,
  formatAlertsTooltipHtml,
  formatSecondsAsMinutes,
  formatStopTypeLabel,
  formatTimeWindow,
  normalizeAlerts,
} from "../../../utils/formatters";
import { toSafeString } from "../../../utils/strings";

/** @typedef {import("../contracts/kanbanApi.contract").KanbanStopItem} KanbanStopItem */

const props = defineProps({
  stopItem: { type: Object, required: true },
  vehicle: {
    type: Object,
    default() {
      return null;
    },
  },
  showOrder: { type: Boolean, default: true },
  showKm: { type: Boolean, default: true },
  showTotalTime: { type: Boolean, default: true },
  showMenu: { type: Boolean, default: true },
  menuDisabled: { type: Boolean, default: false },
  clickable: { type: Boolean, default: true },
  actions: {
    type: Array,
    default() {
      return [];
    },
  },
});

const emit = defineEmits(["open", "action"]);
const numberLabelRef = ref(null);

const orderValue = computed(() => props.stopItem?.order ?? "");
const numberValue = computed(() => {
  const value = props.stopItem?.contact;
  return toSafeString(value?.name);
});
const timeWindow1Value = computed(() =>
  formatTimeWindow(
    props.stopItem?.time_window_1 ?? "",
    (clockValue) => compactClockLabel(clockValue, { alwaysDropMinutes: true })
  )
);
const timeWindow2Value = computed(() =>
  formatTimeWindow(
    props.stopItem?.time_window_2 ?? "",
    (clockValue) => compactClockLabel(clockValue, { alwaysDropMinutes: true })
  )
);
const combinedTimeWindowValue = computed(() => {
  const values = [timeWindow1Value.value, timeWindow2Value.value].filter(
    Boolean
  );
  return values.join(" / ");
});
const orderBadgeValue = computed(() => String(orderValue.value || "").trim());
const numberLabel = computed(() => String(numberValue.value || "").trim());
const hasOrder = computed(
  () => props.showOrder && Boolean(orderBadgeValue.value)
);
const hasNumber = computed(() => Boolean(numberLabel.value));
const kmValue = computed(() =>
  toSafeString(props.stopItem?.km ?? "")
);
const totalTimeSeconds = computed(() =>
  props.stopItem?.total_time ?? ""
);
const totalTimeValue = computed(() =>
  formatSecondsAsMinutes(totalTimeSeconds.value, "0 min")
);
const statusValue = computed(() => props.stopItem?.status || "pendiente");
const statusTooltip = computed(() => {
  const status = String(props.stopItem?.status || "").toUpperCase();
  const description = String(
    props.stopItem?.status_description || ""
  ).trim();

  if (status === "INCIDENCIA" && description) {
    return `Incidencia: ${description}`;
  }

  return "";
});

const hasKmMetric = computed(() => props.showKm && Boolean(kmValue.value));
const hasTotalTimeMetric = computed(
  () =>
    props.showTotalTime &&
    totalTimeSeconds.value !== null &&
    totalTimeSeconds.value !== undefined &&
    String(totalTimeSeconds.value).trim() !== ""
);
const stopTypeValue = computed(() => props.stopItem.type.toUpperCase());
const normalizedStopTypeLabel = computed(
  () => formatStopTypeLabel(props.stopItem) || "Parada"
);
const stopTypeLetter = computed(() => {
  if (props.stopItem?.manual_type === "CARGA") return "C";
  if (props.stopItem?.manual_type === "DESCARGA") return "D";
  if (stopTypeValue.value === "RECOGIDA") return "R";
  if (stopTypeValue.value === "ENTREGA") return "E";
  return "?";
});
const stopTypeTone = computed(() => {
  // CARGA/DESCARGA son RECOGIDA/ENTREGA a efectos de tipo (ver
  // ManualStopBuilder._TYPE_BY_MANUAL_TYPE en el backend) — mismo color,
  // solo cambia la letra mostrada.
  if (props.stopItem?.manual_type === "CARGA" || stopTypeValue.value === "RECOGIDA") {
    return STATUS_TONE_MAP.recogida;
  }
  if (props.stopItem?.manual_type === "DESCARGA" || stopTypeValue.value === "ENTREGA") {
    return STATUS_TONE_MAP.entrega;
  }
  return STATUS_TONE_MAP.pendiente;
});
const stopTypeChipStyle = computed(() => ({
  color: stopTypeTone.value.color,
  backgroundColor: stopTypeTone.value.background,
  borderColor: stopTypeTone.value.border,
}));
const isLoadUnloadStop = computed(
  () =>
    props.stopItem?.manual_type === "CARGA" ||
    props.stopItem?.manual_type === "DESCARGA"
);
const warningMessages = computed(() => {
  return normalizeAlerts(props.stopItem?.alerts ?? []);
});
const warningTooltip = computed(() => {
  if (warningMessages.value.length === 0) return "";
  return formatAlertsTooltipHtml(warningMessages.value);
});
const hasWarning = computed(() => warningMessages.value.length > 0);
const instructionsText = computed(() =>
  String(props.stopItem?.instructions || "").trim()
);
const hasInstructions = computed(() => instructionsText.value.length > 0);
const hasNumberTooltip = ref(false);

const actionsMenuId = computed(
  () =>
    `task-actions-${String(
      props.stopItem?._id || props.stopItem?.kanban_id || "unknown"
    ).replace(/[^a-zA-Z0-9_-]/g, "_")}`
);
const stopActions = computed(() =>
  Array.isArray(props.actions) ? props.actions : []
);
const hasActions = computed(() =>
  stopActions.value.some(
    (group) =>
      Array.isArray(group) &&
      group.some((action) => action && typeof action === "object")
  )
);

function onCardOpen() {
  if (!props.clickable) return;
  emit("open", props.stopItem);
}

function updateNumberTooltipState() {
  const element = numberLabelRef.value;
  if (!element) {
    hasNumberTooltip.value = false;
    return;
  }

  hasNumberTooltip.value = element.scrollWidth > element.clientWidth;
}

function onTaskAction(action) {
  emit("action", {
    action,
    stopItem: props.stopItem,
    vehicle: props.vehicle,
  });
}

let resizeObserver = null;

onMounted(async () => {
  await nextTick();
  updateNumberTooltipState();

  if (typeof ResizeObserver !== "undefined" && numberLabelRef.value) {
    resizeObserver = new ResizeObserver(() => {
      updateNumberTooltipState();
    });
    resizeObserver.observe(numberLabelRef.value);
  }
});

watch(numberLabel, async () => {
  await nextTick();
  updateNumberTooltipState();
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<template>
  <article
    class="stop-route-card"
    :class="{ 'stop-route-card--load-unload': isLoadUnloadStop }"
    @dblclick="onCardOpen"
  >
    <div class="stop-route-head">
      <div v-if="hasOrder || hasNumber" class="stop-route-order-group">
        <argon-status
          v-if="hasOrder"
          entity="route_stop"
          :status="statusValue"
          :text="orderBadgeValue"
          :tooltip-external-text="statusTooltip"
          is-mini
          size="xs"
        />
        <span
          v-if="hasNumber"
          ref="numberLabelRef"
          v-tooltip="hasNumberTooltip ? numberLabel : ''"
          class="stop-route-number route-title"
        >
          {{ numberLabel }}
        </span>
      </div>
      <div class="stop-route-head-side">
        <div v-if="hasTotalTimeMetric" class="stop-route-total-time">
          {{ totalTimeValue }}
        </div>
      </div>
      <div class="stop-route-slot-area">
        <div v-if="showMenu && hasActions" class="stop-route-action-slot">
          <kanban-actions-dropdown
            v-if="showMenu && hasActions"
            class="stop-route-menu-wrap"
            :class="{ 'is-disabled': menuDisabled }"
            :menu-id="actionsMenuId"
            :actions="stopActions"
            aria-label="Acciones de tarea"
            trigger-class="stop-route-actions-button"
            menu-class="stop-route-menu-dropdown"
            hover-reveal-scope=".stop-route-card"
            @action="onTaskAction"
          />
        </div>
      </div>
    </div>
    <div class="stop-route-metrics">
      <div class="metrics-row">
        <div
          v-tooltip="normalizedStopTypeLabel"
          class="metric-chip metric-chip-icon metric-chip-type"
          :style="stopTypeChipStyle"
          :aria-label="normalizedStopTypeLabel"
        >
          <span class="metric-chip-type-letter">{{ stopTypeLetter }}</span>
        </div>
        <div v-if="hasKmMetric" class="metric-chip">
          <span>{{ kmValue }} km</span>
        </div>
        <div
          v-if="combinedTimeWindowValue"
          class="metric-chip"
        >
          <span>{{ combinedTimeWindowValue }}</span>
        </div>
        <div
          v-if="hasInstructions || hasWarning"
          class="metric-chip-trailing-group"
        >
          <div
            v-if="hasInstructions"
            v-tooltip="instructionsText"
            class="metric-chip metric-chip-instructions"
            aria-label="Instrucciones"
          >
            <i class="fas fa-clipboard-list" aria-hidden="true"></i>
          </div>
          <div
            v-if="hasWarning"
            v-tooltip="warningTooltip"
            class="metric-chip metric-chip-warning"
            aria-label="Alertas"
          >
            <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<style scoped lang="scss">
@use "./planner-header-shared";
@use "./planner-surface-shared";

.stop-route-card {
  @extend .planner-soft-surface;
  border: 1px solid var(--planner-border, #dbe2ee);
  border-radius: 10px;
  box-shadow: var(--planner-shadow-sm, 0 1px 2px rgba(16, 24, 40, 0.04));
  cursor: pointer;
  padding: 5px;
  position: relative;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
  user-select: none;
}

.stop-route-card:hover {
  border-color: var(--planner-border-strong, #cbd5e1);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
  z-index: 60;
}

.stop-route-card.kanban-dropdown-open {
  z-index: 210;
}

.stop-route-card--load-unload {
  background-color: rgba(100, 116, 139, 0.04);
}

.stop-route-card:focus-within {
  z-index: 160;
}

.stop-route-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
  position: relative;
}

.stop-route-order-group {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 6px;
  min-width: 0;
  width: 0;
}

.stop-route-head-side {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  margin-left: auto;
  min-width: max-content;
  padding-right: 6px;
}

.stop-route-number {
  display: block;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.stop-route-total-time {
  @extend .planner-meta-time;
}

.stop-route-slot-area {
  align-items: center;
  display: inline-flex;
  justify-content: flex-end;
  min-height: 20px;
  min-width: 20px;
  position: absolute;
  right: 0;
  top: 0;
  width: 20px;
  z-index: 20;
}

.stop-route-action-slot {
  @extend .planner-action-slot;
  z-index: 30;
}

.stop-route-menu-wrap {
  @extend .planner-action-wrap;
}

.stop-route-menu-wrap.is-disabled {
  pointer-events: none;
}

.stop-route-menu-dropdown {
  @extend .planner-action-dropdown;
}

.stop-route-metrics {
  display: flex;
  margin-top: 0;
  min-width: 0;
  overflow: hidden;
}
</style>
